"use client";

import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import reviewsData from "@/data/trustpilotReviews.json";

// Trustpilot Star Component (Green box with white star)
const TrustpilotStars: React.FC<{ count?: number }> = ({ count = 5 }) => {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="w-5 h-5 bg-[#00b67a] flex items-center justify-center rounded-[2px]"
          aria-hidden="true"
        >
          <svg
            className="w-3.5 h-3.5 fill-white"
            viewBox="0 0 24 24"
          >
            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
          </svg>
        </div>
      ))}
    </div>
  );
};

export default function TrustpilotFooterSection() {
  const trustpilotUrl = "https://www.trustpilot.com/review/dailyneedsworld.com";

  return (
    <section className="w-full bg-slate-900 text-white py-10 px-4 md:px-8 border-t border-slate-800">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header Block */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 border-b border-slate-800 pb-6">
          <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
            {/* Trustpilot Logo Header */}
            <div className="flex items-center gap-2">
              <span className="text-xl font-black tracking-tight text-white flex items-center gap-1.5">
                <svg className="w-6 h-6 text-[#00b67a] fill-current" viewBox="0 0 24 24">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                </svg>
                Trustpilot
              </span>
            </div>
            
            <div className="h-4 w-px bg-slate-700 hidden sm:block" />

            <div className="flex items-center gap-3">
              <span className="text-sm font-extrabold text-white">Excellent</span>
              <TrustpilotStars count={5} />
            </div>

            <span className="text-xs text-slate-400 font-medium">
              Rated <strong className="text-white">4.8 / 5</strong> based on customer reviews
            </span>
          </div>

          {/* More Reviews Link */}
          <a
            href={trustpilotUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00b67a] hover:bg-[#009e6a] text-white text-xs font-bold transition-colors shadow-sm shrink-0"
          >
            <span>See our reviews on Trustpilot</span>
            <svg className="w-4 h-4 fill-none stroke-current" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
          </a>
        </div>

        {/* Mobile View: 1-Item Swiper Slider */}
        <div className="block md:hidden">
          <Swiper
            modules={[Autoplay, Pagination]}
            slidesPerView={1}
            spaceBetween={16}
            loop={true}
            autoplay={{ delay: 3500, disableOnInteraction: false }}
            pagination={{ clickable: true }}
            className="pb-10 [&_.swiper-pagination-bullet]:bg-slate-500 [&_.swiper-pagination-bullet-active]:!bg-[#00b67a]"
          >
            {reviewsData.map((review) => (
              <SwiperSlide key={review.id}>
                <div className="bg-slate-800/90 border border-slate-700/80 rounded-xl p-5 flex flex-col justify-between space-y-3 shadow-xs h-full min-h-[180px]">
                  <div className="space-y-2.5">
                    {/* Rating & Date */}
                    <div className="flex items-center justify-between">
                      <TrustpilotStars count={review.rating} />
                      <span className="text-[11px] text-slate-400 font-medium">{review.date}</span>
                    </div>

                    {/* Title */}
                    <h5 className="text-xs font-bold text-white line-clamp-1">
                      {review.title}
                    </h5>

                    {/* Body */}
                    <p className="text-xs text-slate-300 leading-relaxed line-clamp-4 font-normal">
                      &ldquo;{review.content}&rdquo;
                    </p>
                  </div>

                  {/* Author Footer */}
                  <div className="pt-3 border-t border-slate-700/50 flex items-center justify-between text-[11px] text-slate-400">
                    <div className="flex items-center gap-1.5 font-semibold text-slate-200">
                      <span>{review.author}</span>
                      <span className="bg-slate-700 text-slate-300 px-1.5 py-0.2 rounded text-[10px] uppercase font-bold">
                        {review.location}
                      </span>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Desktop View: Grid Layout */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-5">
          {reviewsData.map((review) => (
            <div
              key={review.id}
              className="bg-slate-800/80 border border-slate-700/70 rounded-xl p-5 flex flex-col justify-between space-y-3 hover:border-slate-600 transition-colors shadow-xs"
            >
              <div className="space-y-2.5">
                {/* Rating & Date */}
                <div className="flex items-center justify-between">
                  <TrustpilotStars count={review.rating} />
                  <span className="text-[11px] text-slate-400 font-medium">{review.date}</span>
                </div>

                {/* Title */}
                <h5 className="text-xs font-bold text-white line-clamp-1">
                  {review.title}
                </h5>

                {/* Body */}
                <p className="text-xs text-slate-300 leading-relaxed line-clamp-4 font-normal">
                  &ldquo;{review.content}&rdquo;
                </p>
              </div>

              {/* Author Footer */}
              <div className="pt-3 border-t border-slate-700/50 flex items-center justify-between text-[11px] text-slate-400">
                <div className="flex items-center gap-1.5 font-semibold text-slate-200">
                  <span>{review.author}</span>
                  <span className="bg-slate-700 text-slate-300 px-1.5 py-0.2 rounded text-[10px] uppercase font-bold">
                    {review.location}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Callout */}
        <div className="text-center pt-2">
          <a
            href={trustpilotUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold text-slate-400 hover:text-[#00b67a] transition-colors inline-flex items-center gap-1.5"
          >
            <span className="underline font-bold text-[#00b67a]">View all reviews</span>
          </a>
        </div>

      </div>
    </section>
  );
}
